import SettingsIcon from "@mui/icons-material/Settings";
import { Box, Grid, IconButton, Paper, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { MC_PAGE_BG } from "../utils";
import { QuestionCard } from "./QuestionCard";
import { QuestionDetailModal } from "./QuestionDetailModal";
import { StaffSatisfactionHeader } from "./StaffSatisfactionHeader";
import { SurveySettingsModal } from "./SurveySettingsModal";
import { DEFAULT_SETTINGS, SURVEY_QUESTIONS, SURVEY_RESPONSES } from "./staffSatisfactionData";
import type { SurveyQuestion, SurveySettings, TimeFilter } from "./staffSatisfactionTypes";

export const StaffSatisfactionReport: FC = () => {
	const { track } = useAnalytics();

	const [settings, setSettings] = useState<SurveySettings>(DEFAULT_SETTINGS);
	const [isSettingsOpen, setIsSettingsOpen] = useState(false);
	const [selectedQuestion, setSelectedQuestion] = useState<SurveyQuestion | null>(null);
	const [timeFilter, setTimeFilter] = useState<TimeFilter>("quarter");

	const hasTrackedViewed = useRef(false);
	useEffect(() => {
		if (!hasTrackedViewed.current) {
			track("staff_satisfaction_report_viewed");
			hasTrackedViewed.current = true;
		}
	}, [track]);

	const filteredResponses = useMemo(() => {
		const now = new Date();
		const cutoff = new Date();
		if (timeFilter === "week") cutoff.setDate(now.getDate() - 7);
		else if (timeFilter === "month") cutoff.setMonth(now.getMonth() - 1);
		else cutoff.setMonth(now.getMonth() - 3);
		return SURVEY_RESPONSES.filter((r) => new Date(r.timestamp) >= cutoff);
	}, [timeFilter]);

	const activeQuestions = SURVEY_QUESTIONS.filter((q) => settings.activeQuestionIds.includes(q.id));
	const totalResponses = filteredResponses.length;

	const getResponsesForQuestion = useCallback((questionId: string) => filteredResponses.filter((r) => r.questionId === questionId), [filteredResponses]);

	const handleTimeFilterChange = (_: React.MouseEvent<HTMLElement>, value: TimeFilter | null) => {
		if (!value) return;
		setTimeFilter(value);
		track("staff_satisfaction_time_filter_changed", { filter: value });
	};

	const handleOpenSettings = () => {
		setIsSettingsOpen(true);
		track("staff_satisfaction_settings_opened");
	};

	const handleSaveSettings = (newSettings: SurveySettings) => {
		setSettings(newSettings);
		track("staff_satisfaction_settings_saved");
	};

	const handleOpenQuestion = (question: SurveyQuestion) => {
		setSelectedQuestion(question);
		track("staff_satisfaction_question_opened", { question_id: question.id });
	};

	return (
		<Box
			sx={{
				width: "100%",
				height: "100%",
				bgcolor: MC_PAGE_BG,
				overflow: "auto",
				padding: 2,
				overflowX: "hidden",
			}}
		>
			<StaffSatisfactionHeader />
			{/* Report header with time filter and settings */}
			<Paper
				elevation={0}
				sx={{
					p: 3,
					mb: 3,
					borderRadius: 2,
					bgcolor: "white",
					border: "1px solid",
					borderColor: "divider",
					display: "flex",
					flexDirection: { xs: "column", md: "row" },
					justifyContent: "space-between",
					alignItems: { xs: "flex-start", md: "center" },
					gap: 2,
				}}
			>
				<Typography
					variant="subtitle1"
					sx={{
						color: "text.secondary",
					}}
				>
					{`${totalResponses} Total Responses Recorded`}
				</Typography>

				<Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
					<ToggleButtonGroup value={timeFilter} exclusive onChange={handleTimeFilterChange} aria-label="time filter" size="small">
						<ToggleButton value="week" sx={{ px: 2 }}>
							{"Last Week"}
						</ToggleButton>
						<ToggleButton value="month" sx={{ px: 2 }}>
							{"Last Month"}
						</ToggleButton>
						<ToggleButton value="quarter" sx={{ px: 2 }}>
							{"Last Quarter"}
						</ToggleButton>
					</ToggleButtonGroup>

					<IconButton
						onClick={handleOpenSettings}
						size="large"
						aria-label="Survey Settings"
						sx={{ color: "primary.main", bgcolor: "primary.50", "&:hover": { bgcolor: "primary.100" } }}
					>
						<SettingsIcon />
					</IconButton>
				</Box>
			</Paper>
			{activeQuestions.length === 0 ? (
				<Box
					sx={{
						textAlign: "center",
						py: 8,
						bgcolor: "white",
						borderRadius: 2,
						border: "1px dashed",
						borderColor: "grey.300",
					}}
				>
					<Typography
						variant="h6"
						sx={{
							color: "text.secondary",
						}}
					>
						{"No active questions."}
					</Typography>
					<Typography
						variant="body2"
						sx={{
							color: "text.secondary",
						}}
					>
						{"Open settings to enable questions for the report."}
					</Typography>
				</Box>
			) : (
				<Grid container spacing={3}>
					{activeQuestions.map((question) => (
						<Grid key={question.id} size={12} sx={{ display: "flex" }}>
							<Box sx={{ width: "100%" }}>
								<QuestionCard question={question} responseCount={getResponsesForQuestion(question.id).length} onClick={() => handleOpenQuestion(question)} />
							</Box>
						</Grid>
					))}
				</Grid>
			)}
			<SurveySettingsModal
				open={isSettingsOpen}
				onClose={() => setIsSettingsOpen(false)}
				onSave={handleSaveSettings}
				currentSettings={settings}
				allQuestions={SURVEY_QUESTIONS}
			/>
			<QuestionDetailModal
				open={Boolean(selectedQuestion)}
				onClose={() => setSelectedQuestion(null)}
				question={selectedQuestion}
				responses={selectedQuestion ? getResponsesForQuestion(selectedQuestion.id) : []}
			/>
		</Box>
	);
};
